import { useState } from 'react';
import { z } from 'zod';

import { FeedbackChip } from '@/components/FeedbackChips';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Button, Input } from '@/design-system';
import { useColorScheme } from '@/hooks/useColorScheme';
import { ExperimentViewOverridesSchema } from '@/types/schema';

import { FeedbackDirectionToggle } from '../BaselineSessionSelectPopover';
import { getHeatmapStyleFromPercentage } from '../SingleExperimentColumns/utils/getHeatmapStyleFromPercentage';
import {
  convertColorStopInputsToColorStops,
  validateColorStopPositions,
  validateNumericInput,
} from './utils/colors';

type ColorStop = [number, string];
type ColorStopInput = [string, string]; // For internal state with string positions

const ColorGradientSchema = z
  .array(z.tuple([z.number(), z.enum(['red', 'gray', 'green'])]))
  .length(3);

export function EditHeatmapPopover({
  feedbackKey,
  gradient,
  onChange,
  onValidationChange,
}: {
  feedbackKey: string;
  gradient?: ExperimentViewOverridesSchema['column_overrides'][number]['color_gradient'];
  formSessionId?: string;
  onChange?: (gradient: ColorStop[] | undefined) => void;
  onValidationChange?: (errors: string[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { isDarkMode } = useColorScheme();

  const resolveValidGradient = (): ColorStopInput[] => {
    const inputGradient = ColorGradientSchema.safeParse(gradient);
    if (inputGradient.success) {
      return inputGradient.data.map(([pos, color]) => [pos.toString(), color]);
    } else {
      return [
        ['0', 'red'],
        ['0.5', 'gray'],
        ['1', 'green'],
      ];
    }
  };

  const [colorStopsInput, setColorStopsInput] = useState<ColorStopInput[]>(
    resolveValidGradient()
  );

  // Convert string positions to numbers for calculations
  const colorStops = convertColorStopInputsToColorStops(colorStopsInput);

  const lowColorStop = colorStops[0];
  const midColorStop = colorStops[1];
  const highColorStop = colorStops[2];

  const isLowerBetter = lowColorStop[1] === 'green';

  const handlePositionChange = (index: number, value: string) => {
    const validation = validateNumericInput(value);

    if (!validation.isValid) {
      return;
    }

    // Update the input state
    const newColorStopsInput = [...colorStopsInput];
    newColorStopsInput[index] = [value, newColorStopsInput[index][1]];
    setColorStopsInput(newColorStopsInput);

    // Convert to numeric for validation and parent callback
    const newColorStops =
      convertColorStopInputsToColorStops(newColorStopsInput);

    // Validate color stop positions and collect all errors
    const allErrors: string[] = [];
    for (let i = 0; i < newColorStops.length; i++) {
      const error = validateColorStopPositions(newColorStops, i);
      if (error) {
        allErrors.push(error);
      }
    }

    const hasErrors = allErrors.length > 0;
    onValidationChange?.(allErrors);

    // Only call parent onChange if there are no validation errors
    if (!hasErrors) {
      const newMinScore = newColorStops[0][0];
      const newMidpoint = newColorStops[1][0];
      const newMaxScore = newColorStops[2][0];

      setPreviewScores([
        newMinScore,
        newMinScore + (newMidpoint - newMinScore) / 2,
        newMidpoint,
        newMidpoint + (newMaxScore - newMidpoint) / 2,
        newMaxScore,
      ]);

      onChange?.(newColorStops);
    }
  };

  const colorStopStyles = colorStops.map(([score]) => {
    const numScore = typeof score === 'string' ? parseFloat(score) : score;
    return getHeatmapStyleFromPercentage({
      colorMode: isDarkMode ? 'dark' : 'light',
      score: numScore,
      isLowerBetter,
      colorGradient: colorStops.map(([pos, color]) => [
        typeof pos === 'string' ? parseFloat(pos) : pos,
        color,
      ]) as [number, string][],
    });
  });

  const minScore = lowColorStop[0];
  const midpoint = midColorStop[0];
  const maxScore = highColorStop[0];

  const [previewScores, setPreviewScores] = useState([
    minScore,
    minScore + (midpoint - minScore) / 2,
    midpoint,
    midpoint + (maxScore - midpoint) / 2,
    maxScore,
  ]);

  const gradientPreview = `linear-gradient(to right, ${colorStopStyles
    .map(({ rawColorVar }, i) => {
      const minValue = lowColorStop[0];
      const maxValue = highColorStop[0];
      const currentValue = colorStops[i][0];
      const percentage =
        maxValue === minValue
          ? 0
          : (currentValue - minValue) / (maxValue - minValue);
      return `${rawColorVar} ${percentage * 100}%`;
    })
    .join(', ')})`;

  const hasErrors = colorStops.some(
    (_, i) => validateColorStopPositions(colorStops, i) !== null
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="plain"
          color="secondary"
          size="sm"
          onClick={() => setIsOpen(true)}
          className={`h-8 w-16 border p-0 ${
            hasErrors ? 'border-red-500' : 'border-secondary'
          }`}
        >
          <div className="flex w-full items-center justify-center gap-2">
            <div
              className="h-2 w-10 rounded"
              style={{ background: gradientPreview }}
            />
          </div>
        </Button>
      </PopoverTrigger>

      {isOpen && (
        <PopoverContent className="w-108" align="end">
          <div className="flex flex-col gap-3">
            <div className="absolute left-0 top-2 flex w-full items-center justify-between border-x border-secondary bg-secondary py-1 pl-4">
              <h3 className="text-sm font-semibold uppercase">
                Heat map for {feedbackKey}
              </h3>
            </div>

            <div className="mt-10 flex w-full items-center justify-between">
              <div className="text-sm font-semibold text-secondary">
                Higher = better
              </div>
              <FeedbackDirectionToggle
                isLowerScoreBetter={isLowerBetter}
                setIsLowerScoreBetter={(newValue) => {
                  const isLowerBetter = newValue === 'No';
                  const newColorStopsInput = colorStopsInput.map(
                    ([pos, color], i) => {
                      if (i === 0) {
                        return [pos, isLowerBetter ? 'green' : 'red'];
                      } else if (i === 2) {
                        return [pos, isLowerBetter ? 'red' : 'green'];
                      }
                      return [pos, color];
                    }
                  ) as ColorStopInput[];
                  setColorStopsInput(newColorStopsInput);

                  // Convert to numeric for parent callback
                  const newColorStops =
                    convertColorStopInputsToColorStops(newColorStopsInput);
                  onChange?.(newColorStops);
                }}
                isMutating={false}
              />
            </div>

            <div className="mt-2">
              <div className="mb-1 text-sm font-semibold text-secondary">
                Color thresholds
              </div>
              <div className="flex max-h-48 gap-2">
                {['Low point', 'Mid point', 'High point'].map(
                  (label, index) => (
                    <div key={index} className="flex flex-col gap-2 text-xs">
                      <span className="text-secondary">{label}</span>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-4 w-4 rounded-md"
                          style={{
                            background: colorStopStyles[index].rawColorVar,
                          }}
                        ></div>
                        <Input
                          size="sm"
                          value={colorStopsInput[index][0]}
                          onChange={(e) => handlePositionChange(index, e)}
                          isError={
                            !!validateColorStopPositions(colorStops, index)
                          }
                          className="w-24"
                        />
                      </div>
                    </div>
                  )
                )}
              </div>
              {colorStops.some(
                (_, i) => validateColorStopPositions(colorStops, i) !== null
              ) && (
                <div className="text-xs text-red-500">
                  <br />
                  {colorStops
                    .map((_, i) => validateColorStopPositions(colorStops, i))
                    .filter(Boolean)
                    .map((error, index, array) => (
                      <span key={index}>
                        {error}
                        {index < array.length - 1 && <br />}
                      </span>
                    ))}
                </div>
              )}
            </div>
            <div className="mt-2">
              <div className="flex flex-col gap-2">
                <div className="text-sm font-semibold text-secondary">
                  Preview
                </div>
                <div className="flex w-full gap-2">
                  {previewScores.map((score, i) => {
                    const { background, text, hover } =
                      getHeatmapStyleFromPercentage({
                        colorMode: isDarkMode ? 'dark' : 'light',
                        score,
                        isLowerBetter,
                        colorGradient: colorStops,
                      });
                    return (
                      <FeedbackChip
                        key={`chip-${score}-${i}`}
                        allowTruncation={true}
                        hideFeedbackKey={true}
                        hoverHighlight={true}
                        isCategorical={false}
                        feedback_key={'PREVIEW_VALUE'}
                        value={[score]}
                        className={`flex grow justify-center rounded-md border-none ${background} ${hover} cursor-pointer`}
                        valueClassname={text}
                        errors={0}
                        startDecorator={<></>}
                        columnOverride={{
                          column: 'PREVIEW_VALUE',
                          color_gradient: colorStops,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}
